import React, { useEffect, useState } from "react";
import { Box, Button, Grid, TextField, Typography, Switch, FormControlLabel, CircularProgress, Alert } from "@mui/material";
import { Save } from "@mui/icons-material";
import { toast } from "react-toastify";
import { aiCallApi, formatBdt, formatNumber } from "./useAICalling";
import { SectionCard, Metric } from "./AICallingComponents";

/**
 * New-signup campaign: follow-up calls to hot/warm members and the personal wallet offer the AI may
 * give on a follow-up (backend utils/AICalling/signupCalls.js), with what they produced.
 */
const toForm = (s) => ({
  enabled: !!s?.enabled,
  max: String(s?.max ?? 2),
  offerEnabled: !!s?.offer?.enabled,
  payAmount: String(s?.offer?.payAmount ?? 2000),
  bonus: String(s?.offer?.bonus ?? 1000),
  validHours: String(s?.offer?.validHours ?? 48),
});

const pct = (a, b) => (b ? `${Math.round((a / b) * 1000) / 10}%` : "—");

const SignupFollowUpsCard = ({ campaignId, signup, onSaved }) => {
  const saved = signup?.settings;
  const savedKey = JSON.stringify(saved || {});
  const [form, setForm] = useState(toForm(saved));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(toForm(saved));
    // only when the saved settings change, not on every 15 s refresh
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedKey]);

  if (!signup) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const dirty = JSON.stringify(form) !== JSON.stringify(toForm(saved));

  const save = async () => {
    setSaving(true);
    const res = await aiCallApi.campaignFollowUps(campaignId, {
      enabled: form.enabled,
      max: Number(form.max),
      offer: { enabled: form.offerEnabled, payAmount: Number(form.payAmount), bonus: Number(form.bonus), validHours: Number(form.validHours) },
    });
    setSaving(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(res.message || "Saved");
    onSaved?.();
  };

  const i = signup.interest || {};
  const answered = (i.hot || 0) + (i.warm || 0) + (i.cold || 0) + (i.unknown || 0);
  const fu = signup.followUps || {};
  const fuLine = (n) => {
    const r = fu[`followUp${n}`] || {};
    const total = Object.values(r).reduce((s, v) => s + v, 0);
    return total ? `${formatNumber(total)} (answered ${formatNumber(r.sent || 0)}, waiting ${formatNumber((r.queued || 0) + (r.calling || 0))})` : "0";
  };
  const o = signup.offers || {};
  const s = signup.sales || {};
  const called = s.called || {};
  const holdout = s.holdout || {};
  const total = Number(form.payAmount || 0) + Number(form.bonus || 0);

  return (
    <SectionCard
      title="Follow-ups and special offer"
      subtitle="Members the AI marked hot (agreed / link sent) or warm (interested, not now) get follow-up calls: hot after 1 day, warm after 2 days, at most 7 days after the first call. They stop when the member pays, goes cold or asks not to be called."
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Metric label="Hot" value={formatNumber(i.hot || 0)} sub={`of ${formatNumber(answered)} answered calls`} color="success.main" />
        <Metric label="Warm" value={formatNumber(i.warm || 0)} sub="interested, not now" color="warning.main" />
        <Metric label="Cold" value={formatNumber(i.cold || 0)} sub={i.unknown ? `${formatNumber(i.unknown)} not marked` : "not interested"} />
        <Metric label="Offers" value={`${formatNumber(o.used || 0)} / ${formatNumber(o.given || 0)}`} sub={`used / given · bonus paid ${formatBdt(o.bonus || 0)}`} />
        <Metric
          label="Called → paid"
          value={pct(called.paid || 0, called.members || 0)}
          sub={`${formatNumber(called.paid || 0)} of ${formatNumber(called.members || 0)} · ${formatBdt(called.taka || 0)} · ${formatNumber(called.afterFollowUp || 0)} after a follow-up`}
          color="success.main"
        />
        <Metric
          label="Not called (holdout) → paid"
          value={pct(holdout.paid || 0, holdout.members || 0)}
          sub={`${formatNumber(holdout.paid || 0)} of ${formatNumber(holdout.members || 0)} · ${formatBdt(holdout.taka || 0)}`}
        />
        <Metric label="Follow-up 1" value={fuLine(1)} md={3} />
        <Metric label="Follow-up 2" value={fuLine(2)} md={3} />
      </Grid>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        Paid = a bKash payment (contact, package or wallet top-up) within {formatNumber(signup.attributionDays || 7)} days of the member's first call.
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel control={<Switch checked={form.enabled} onChange={set("enabled")} />} label="Follow-up calls on" />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField label="Follow-ups per member" type="number" size="small" fullWidth value={form.max} onChange={set("max")} inputProps={{ min: 0, max: 3, step: 1 }} helperText="0–3 (plan: 2)" />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel control={<Switch checked={form.offerEnabled} onChange={set("offerEnabled")} />} label="AI may give the special offer on a follow-up" />
          <Typography variant="caption" color="text.secondary" display="block">
            Only on follow-ups, only when price stops them, once per member. It is a personal wallet offer: they pay into the wallet
            with bKash, get the bonus, then buy the yearly offer (or any package) with "ব্যালেন্স". The website shows it on their home page
            and wallet, and they get one SMS reminder 6 hours before it ends.
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Pay into wallet (৳)" type="number" size="small" fullWidth value={form.payAmount} onChange={set("payAmount")} disabled={!form.offerEnabled} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Bonus (৳)" type="number" size="small" fullWidth value={form.bonus} onChange={set("bonus")} disabled={!form.offerEnabled}
            helperText={form.offerEnabled ? `They get ${formatBdt(total)} in the wallet` : ""} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField label="Offer lasts (hours)" type="number" size="small" fullWidth value={form.validHours} onChange={set("validHours")} disabled={!form.offerEnabled} />
        </Grid>
      </Grid>
      {form.offerEnabled && total !== 3000 && (
        <Alert severity="info" sx={{ mt: 2 }}>The yearly offer costs ৳3,000 at the normal price; with these amounts the wallet gets {formatBdt(total)}.</Alert>
      )}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />} onClick={save} disabled={!dirty || saving}>
          Save
        </Button>
      </Box>
    </SectionCard>
  );
};

export default SignupFollowUpsCard;
